import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faFileInvoice, faCalendarAlt, faHandshake, faMoneyBillWave, faFileAlt, faSearch, faBox, faTruck, faIdCard, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import { crmService } from '../../services/crmService';
import { dashboardService } from '../../services/dashboardService';
import { useUserInfo } from '../../contexts/UserContext';
import { resolveIssuerName } from '../../utils/issuerDisplay';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import InvoiceValidationModal, { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import './OutgoingInvoice.css';

/**
 * Screen 1: Header & Supplier Information for PURCHASE ORDERS
 * 
 * Fields:
 * - HMS Entity (dropdown or from delivery)
 * - Issuer/Creator (automatic - logged in user)
 * - PO Number (auto-generated PO-XXXXXXXX after save)
 * - PO Date (defaults to today)
 * - Internal Reference (delivery number, contract numbers, purchase recap numbers)
 * - Supplier Name (from CRM or Delivery, option to edit)
 * - Creditor Nr (from CRM Partner, read-only)
 * - Expected Delivery Date (from delivery or manual)
 * - Payment Terms (dropdown)
 * - Supply Reference (text field)
 */

const PurchaseOrderScreen1 = ({ 
  poData, 
  setPoData, 
  onNext, 
  sourceDelivery = null,
  isReadOnly = false,
  issuerName = null,
  isPersisted = false
}) => {
  const [loading, setLoading] = useState(true);
  const [hmsEntities, setHmsEntities] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [partners, setPartners] = useState([]);
  
  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationFieldIds, setValidationFieldIds] = useState([]);
  
  // Helper function to parse internal reference into separate parts
  const parseInternalReference = (internalRef) => {
    if (!internalRef) return { delivery: '', contracts: '', purchaseRecaps: '' };
    
    const lines = internalRef.split('\n').map(line => line.trim()).filter(line => line);
    let delivery = '';
    let contracts = [];
    let purchaseRecaps = '';
    
    lines.forEach(line => {
      if (line.startsWith('Delivery:')) {
        delivery = line.replace('Delivery:', '').trim();
      } else if (line.includes('Contract:')) {
        contracts.push(line.replace('Seller Contract:', '').replace('Buyer Contract:', '').trim());
      } else if (line.startsWith('Purchase Recaps:')) {
        purchaseRecaps = line.replace('Purchase Recaps:', '').trim();
      }
    });
    
    return {
      delivery,
      contracts: contracts.join(', '),
      purchaseRecaps
    };
  };
  
  // Update internal reference from separate fields
  const updateInternalReference = (field, value) => {
    const current = parseInternalReference(poData.internal_reference);
    current[field] = value;
    
    const lines = [];
    if (current.delivery) lines.push(`Delivery: ${current.delivery}`);
    if (current.contracts) lines.push(`Contracts: ${current.contracts}`);
    if (current.purchaseRecaps) lines.push(`Purchase Recaps: ${current.purchaseRecaps}`);
    
    setPoData(prev => ({ ...prev, internal_reference: lines.join('\n') }));
  };
  
  // Partner search with debouncing
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [debouncedPartnerSearch, setDebouncedPartnerSearch] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);
  const [selectedPartnerAddress, setSelectedPartnerAddress] = useState('');
  const partnerSearchRef = useRef(null);
  
  // Get current user info
  const { userInfo, updateUserInfo } = useUserInfo();
  const [sessionUserName, setSessionUserName] = useState('');
  
  useEffect(() => {
    loadDropdownData();
    loadIssuerInfo();
  }, []);
  
  const loadIssuerInfo = async () => {
    try {
      // Try from context first
      if (userInfo.first_name && userInfo.last_name) {
        setSessionUserName(`${userInfo.first_name} ${userInfo.last_name}`.trim());
      } else {
        // Fetch from API if context is empty (page reload)
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
        setSessionUserName(`${userInfoData.first_name} ${userInfoData.last_name}`.trim());
      }
    } catch (error) {
      console.error('Error loading issuer info:', error);
      setSessionUserName('Automatic (person creating the PO)');
    }
  };

  // The Issuer names whoever RAISED this document, which is not whoever is
  // looking at it -- only an unsaved document belongs to the person typing.
  const issuerFullName = resolveIssuerName({
    isPersisted,
    storedIssuerName: issuerName,
    sessionUserName,
  });
  
  useEffect(() => {
    // Initialize partner search term from saved data
    if (poData.supplier_name && !partnerSearchTerm) {
      setPartnerSearchTerm(poData.supplier_name);
    }
  }, [poData.supplier_name]);
  
  useEffect(() => {
    // Set PO date to today if not already set
    if (!poData.po_date) {
      const today = new Date().toISOString().split('T')[0];
      setPoData(prev => ({ ...prev, po_date: today }));
    }
  }, []);
  
  // Debounce partner search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPartnerSearch(partnerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [partnerSearchTerm]);
  
  // Fetch partners when debounced search changes
  useEffect(() => {
    if (debouncedPartnerSearch.trim().length >= 2) {
      fetchApprovedPartners(debouncedPartnerSearch);
    } else {
      setFilteredPartners([]);
    }
  }, [debouncedPartnerSearch]);
  
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partnerSearchRef.current && !partnerSearchRef.current.contains(event.target)) {
        setShowPartnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch all required dropdown data
      const [entitiesData, dropdownsData] = await Promise.all([
        fetchHMSEntities(),
        invoicingService.fetchInvoicingDropdowns()
      ]);
      
      setHmsEntities(entitiesData);
      setPaymentTerms(dropdownsData.payment_terms || []);
      
      // If creating from delivery, pre-fill data
      if (sourceDelivery) {
        prefillFromDelivery(sourceDelivery);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setLoading(false);
    }
  };
  
  const prefillFromDelivery = (delivery) => {
    setPoData(prev => ({
      ...prev,
      hms_entity: delivery.hms_entity,
      supplier_name: delivery.delivery_supplier_name || delivery.delivery_buyer_crm_partner_name,
      expected_delivery_date: delivery.delivery_date,
      internal_reference: `Delivery: ${delivery.delivery_unique_number}\n` +
                         (delivery.seller_contract_number ? `Contract: ${delivery.seller_contract_number}` : ''),
      currency: delivery.sale_price_per_ton_contract_currency || 'USD',
    }));
    
    if (delivery.delivery_supplier_name) {
      setPartnerSearchTerm(delivery.delivery_supplier_name);
    }
  };
  
  const fetchApprovedPartners = async (searchTerm) => {
    try {
      setPartnerSearchLoading(true);
      
      // Use the SAME method as Outgoing Invoice screen
      const results = await crmService.searchProfilesByName(searchTerm);
      
      // Filter for APPROVED profiles only (client-side since API doesn't support it)
      const approvedProfiles = results.filter(profile => 
        profile.approval_status === 'APPROVED' || profile.approval_status === undefined
      );
      
      // Map to the format expected by the dropdown
      const partnersData = approvedProfiles.map(profile => ({
        id: profile.id,
        legal_entity_name: profile.legal_entity_name,
        trading_short_name: profile.trading_short_name,
        head_office_address: profile.head_office_address || '',
        display_name: profile.trading_short_name
          ? `${profile.legal_entity_name} (${profile.trading_short_name})`
          : profile.legal_entity_name
      }));
      
      setFilteredPartners(partnersData);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setFilteredPartners([]);
    } finally {
      setPartnerSearchLoading(false);
    }
  };

  const handleSelectPartner = async (partner) => {
    console.log('=== SUPPLIER SELECTED ===');
    console.log('Partner:', partner);
    console.log('Partner ID:', partner.id);
    console.log('Before update - poData.supplier:', poData.supplier);
    setPoData(prev => {
      console.log('Inside setPoData - prev.supplier:', prev.supplier);
      const updated = { 
        ...prev, 
        supplier: partner.id,
        supplier_name: partner.legal_entity_name
      };
      console.log('After update - supplier:', updated.supplier);
      return updated;
    });
    setPartnerSearchTerm(partner.legal_entity_name);
    setSelectedPartnerAddress(partner.head_office_address || '');
    setShowPartnerDropdown(false);

    // Fetch creditor number from partner
    try {
      const partnerDetails = await crmService.getProfile(partner.id);
      if (partnerDetails.creditor_number) {
        setPoData(prev => ({ 
          ...prev, 
          creditor_number: partnerDetails.creditor_number 
        }));
      }
    } catch (error) {
      console.error('Error fetching partner details:', error);
    }
  };
  
  const handleNext = () => {
    // No validation on navigation - users can freely move between screens
    // Validation only happens on Save/Submit in Screen 3
    onNext();
  };
  
  if (loading) {
    return (
      <div className="invoice-loading">
        <div className="loading-spinner"></div>
        <p>Loading purchase order data...</p>
      </div>
    );
  }
  
  return (
    <>
      <InvoiceValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        title="Complete Required Fields"
        errors={validationErrors}
        fieldIds={validationFieldIds}
      />
      
      <div className="invoice-screen-content">
        <div className="invoice-form-section">
          <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <FontAwesomeIcon icon={faBuilding} /> Purchase Order Header & Supplier Information
            </span>
            <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
              {poData.po_number ? `PO #: ${poData.po_number}` : '(PO number will be generated on save)'}
            </span>
          </h2>
          
          <div className="invoice-form-grid">
            {/* Left Column */}
            <div className="invoice-form-column">
              {/* IMT Entity */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faBuilding} /> Entity
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {hmsEntities.find(e => e.value === poData.hms_entity)?.label || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="po_hms_entity_select"
                    className="form-select"
                    value={poData.hms_entity || ''}
                    onChange={(e) => {
                      const newValue = parseInt(e.target.value);
                      console.log('=== HMS ENTITY SELECTED ===');
                      console.log('Raw value:', e.target.value);
                      console.log('Parsed value:', newValue);
                      console.log('Before update - poData.hms_entity:', poData.hms_entity);
                      setPoData(prev => {
                        console.log('Inside setPoData - prev.hms_entity:', prev.hms_entity);
                        const updated = { ...prev, hms_entity: newValue };
                        console.log('After update - hms_entity:', updated.hms_entity);
                        return updated;
                      });
                      clearFieldError('po_hms_entity_select');
                    }}
                    disabled={sourceDelivery}
                  >
                    <option value="">Select Entity...</option>
                    {hmsEntities.map(entity => (
                      <option key={entity.value} value={entity.value}>
                        {entity.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Issuer/Creator */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faUser} /> Creator
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={issuerFullName}
                  readOnly
                  disabled
                />
              </div>
              
              {/* PO Number - Only show if it exists (after save) */}
              {poData.po_number && (
                <div className="form-field">
                  <label className="field-label">
                    <FontAwesomeIcon icon={faFileInvoice} /> PO Number
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={poData.po_number}
                    readOnly
                    disabled
                  />
                </div>
              )}
              
              {/* PO Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={poData.po_date || ''}
                  onChange={(e) => {
                    setPoData(prev => ({ ...prev, po_date: e.target.value }));
                    clearFieldError('po_date');
                  }}
                  name="po_date"
                  id="po_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} /> PO Date
                    </span>
                  }
                  placeholder="Select PO date"
                  required={true}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Internal Reference - 3 separate fields */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Internal Reference
                </label>
                <div className="internal-reference-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="delivery number"
                    value={parseInternalReference(poData.internal_reference).delivery}
                    onChange={(e) => updateInternalReference('delivery', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="contracts number"
                    value={parseInternalReference(poData.internal_reference).contracts}
                    onChange={(e) => updateInternalReference('contracts', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="purchase recap numbers"
                    value={parseInternalReference(poData.internal_reference).purchaseRecaps}
                    onChange={(e) => updateInternalReference('purchaseRecaps', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="invoice-form-column">
              {/* Supplier Name - Searchable CRM Profile Dropdown */}
              <div className="form-field" ref={partnerSearchRef}>
                <label className="field-label required">
                  <FontAwesomeIcon icon={faHandshake} /> Supplier (CRM Profile)
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {poData.supplier_name || 'N/A'}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'relative' }}>
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        style={{ 
                          position: 'absolute', 
                          left: '12px', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#666',
                          pointerEvents: 'none'
                        }} 
                      />
                      <input
                        type="text"
                        id="po_supplier_search_input"
                        className="form-input"
                        style={{ paddingLeft: '36px' }}
                        value={partnerSearchTerm}
                        onChange={(e) => {
                          setPartnerSearchTerm(e.target.value);
                          setSelectedPartnerAddress('');
                          setShowPartnerDropdown(true);
                          clearFieldError('po_supplier_search_input');
                        }}
                        onFocus={() => setShowPartnerDropdown(true)}
                        placeholder="Search APPROVED CRM profiles by Legal Entity Name..."
                        disabled={sourceDelivery}
                      />
                    </div>
                    
                    {showPartnerDropdown && debouncedPartnerSearch.trim().length >= 2 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {partnerSearchLoading ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            Searching...
                          </div>
                        ) : filteredPartners.length > 0 ? (
                          filteredPartners.map(partner => (
                            <div
                              key={partner.id}
                              onClick={() => handleSelectPartner(partner)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div style={{ fontWeight: '600', color: '#4a6b25' }}>
                                {partner.legal_entity_name}
                              </div>
                              {partner.trading_short_name && (
                                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                                  Trading as: {partner.trading_short_name}
                                </div>
                              )}
                              {partner.head_office_address && (
                                <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <FontAwesomeIcon icon={faMapMarkerAlt} /> {partner.head_office_address}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                            No APPROVED partners found for "{debouncedPartnerSearch}"
                          </div>
                        )}
                      </div>
                    )}
                    {poData.supplier && selectedPartnerAddress && (
                      <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedPartnerAddress}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Creditor Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faIdCard} /> Creditor Nr
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={poData.creditor_number || '(from CRM)'}
                  readOnly
                  disabled
                />
              </div>
              
              {/* Expected Delivery Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={poData.expected_delivery_date || ''}
                  onChange={(e) => {
                    setPoData(prev => ({ ...prev, expected_delivery_date: e.target.value }));
                    clearFieldError('expected_delivery_date');
                  }}
                  name="expected_delivery_date"
                  id="expected_delivery_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faTruck} /> Expected Delivery Date
                    </span>
                  }
                  placeholder="Select expected delivery date"
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Payment Terms */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Terms
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {paymentTerms.find(t => t.id === poData.payment_terms)?.term_code || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="po_payment_terms_select"
                    className="form-select"
                    value={poData.payment_terms || ''}
                    onChange={(e) => {
                      setPoData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) }));
                      clearFieldError('po_payment_terms_select');
                    }}
                  >
                    <option value="">Select Payment Terms...</option>
                    {paymentTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.term_code} - {term.description}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Supply Reference */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Supply Reference
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {poData.supply_reference || 'N/A'}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={poData.supply_reference || ''}
                    onChange={(e) => setPoData(prev => ({ ...prev, supply_reference: e.target.value }))}
                    placeholder="Enter supply reference..."
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="screen-navigation" style={{ justifyContent: 'flex-end' }}>
            <button className="btn-primary" type="button" onClick={handleNext}>
              Next: Line Items <FontAwesomeIcon icon={faBox} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderScreen1;
